import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  useTheme,
  Divider,
  Chip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  AccessTime as AccessTimeIcon,
  GpsFixed as GpsIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import RegisterOrgForm from '../../components/auth/RegisterOrgForm.jsx'
import { StatusChip, SubscriptionChip } from '../../components/common/Chips'
import UsersManagement from './UsersManagement.jsx'
import DeviceManager from '../../components/Devices/DeviceManager'
import { organizationApi } from '../../api/organizationApi'
import { formatDate } from '../../utils/formatters'

// Small embedded Leaflet map to preview the geofence
const GeofenceMapPreview = ({ latitude, longitude, radius }) => {
  const mapRef = useRef(null)
  const instanceRef = useRef(null)

  useEffect(() => {
    if (!mapRef.current || instanceRef.current) return
    if (!latitude || !longitude) return

    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      document.head.appendChild(link)
    }

    import('leaflet').then(({ default: L }) => {
      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      })

      if (!mapRef.current) return
      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false })
        .setView([latitude, longitude], 16)

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19,
      }).addTo(map)

      L.marker([latitude, longitude]).addTo(map)
      L.circle([latitude, longitude], { radius, color: '#1976d2', fillOpacity: 0.15 }).addTo(map)

      instanceRef.current = map
    })

    return () => {
      if (instanceRef.current) {
        instanceRef.current.remove()
        instanceRef.current = null
      }
    }
  }, [latitude, longitude, radius])

  if (!latitude || !longitude) return null

  return (
    <Box
      ref={mapRef}
      sx={{ height: 260, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mt: 2 }}
    />
  )
}

const OrganizationDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')
  const [attendanceData, setAttendanceData] = useState([])
  const [attendanceLoading, setAttendanceLoading] = useState(false)

  React.useEffect(() => {
    fetchOrganizationDetails()
  }, [id])

  const fetchOrganizationDetails = async () => {
    try {
      setLoading(true)
      const response = await organizationApi.getOrganizationDetails(id)
      setOrganization(response.data)
    } catch (error) {
      console.error('Failed to fetch organization details:', error)
      setSnackbarMessage('Failed to fetch organization details')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchAttendanceData = async () => {
    try {
      setAttendanceLoading(true)
      const response = await organizationApi.getOrgWeeklySummary(id)
      setAttendanceData(response.data)
    } catch (error) {
      console.error('Failed to fetch attendance data:', error)
      setSnackbarMessage('Failed to fetch attendance data')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setAttendanceLoading(false)
    }
  }

  React.useEffect(() => {
    if (tabValue === 3) {
      fetchAttendanceData()
    }
  }, [tabValue, id])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleEditSuccess = (updatedData) => {
    setOrganization(prev => ({ ...prev, ...updatedData }))
    setEditDialogOpen(false)
    setSnackbarMessage('Organization updated successfully!')
    setSnackbarSeverity('success')
    setSnackbarOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await organizationApi.deleteOrganization(id)
      setOrganization(prev => ({ ...prev, status: 'inactive' }))
      setSnackbarMessage('Organization deactivated successfully!')
      setSnackbarSeverity('success')
      setSnackbarOpen(true)
    } catch (error) {
      console.error('Failed to deactivate organization:', error)
      setSnackbarMessage('Failed to deactivate organization')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setDeleteDialogOpen(false)
    }
  }

  if (loading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>
  }

  if (!organization) {
    return <Box sx={{ p: 3 }}>Organization not found</Box>
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Edit Organization Dialog */}
      <RegisterOrgForm
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
        editMode={true}
        initialData={organization}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} disableRestoreFocus>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All data associated with this organization will be permanently deleted.
          </Alert>
          Are you sure you want to delete <strong>{organization.name}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="h4" fontWeight="bold" noWrap>
                  {organization.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Organization ID: #{organization.id}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, mt: { xs: 1, md: 0 } }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={organization.status !== 'active'}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Delete
              </Button>
            </Box>
          </Box>

          {/* Status Bar */}
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
            <Grid container spacing={{ xs: 1, md: 2 }} alignItems="center">
              <Grid size="auto">
                <StatusChip status={organization.status} />
              </Grid>
              {organization.subscription_plan && (
                <Grid size="auto">
                  <SubscriptionChip subscription={organization.subscription_plan} />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Typography variant="body2" color="textSecondary">
                  Created: {formatDate(organization.created_at)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Typography variant="body2" color="textSecondary">
                  Last Updated: {formatDate(organization.updated_at)}
                </Typography>
              </Grid>
              <Grid size="grow" />

            </Grid>
          </Paper>
        </Box>

        {/* Unified Card for Tabs and Content */}
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: 'none' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Users" />
            <Tab label="Devices" />
            <Tab label="Attendance" />
            <Tab label="Settings" />
          </Tabs>

          {/* Tab Content Area */}
          <Box sx={{ p: tabValue === 0 ? { xs: 1.5, md: 3 } : 0 }}>
            {tabValue === 0 && (
              <Grid container spacing={3}>
                {/* Left Column - Organization Info */}
                <Grid size={{ xs: 12 }}>
                  <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 3 }, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Organization Information
                    </Typography>

                    <Grid container spacing={{ xs: 2, md: 4 }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <List disablePadding>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <BusinessIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Organization Name"
                              secondary={
                                <Box>
                                  <Typography variant="body1" fontWeight="medium">
                                    {organization.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {organization.org_code}
                                  </Typography>
                                </Box>
                              }
                              primaryTypographyProps={{ variant: 'body2', color: 'textSecondary', sx: { mb: 0.5 } }}
                              secondaryTypographyProps={{ component: 'div', sx: { wordBreak: 'break-word' } }}
                            />
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <LocationIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Address"
                              secondary={organization.address}
                            />
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <AccessTimeIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Created At"
                              secondary={formatDate(organization.created_at)}
                            />
                          </ListItem>
                        </List>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <List disablePadding>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <PeopleIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Contact Person"
                              secondary={organization.contact_name}
                            />
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <EmailIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Email"
                              secondary={organization.contact_email}
                            />
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <PhoneIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Phone"
                              secondary={organization.contact_phone}
                            />
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <CalendarIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Subscription Plan"
                              secondary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                    {organization.subscription_plan}
                                  </Typography>
                                  <StatusChip status={organization.status} size="small" />
                                </Box>
                              }
                            />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Geofence Section */}
                    {(organization.latitude || organization.geofence_radius) && (
                      <>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Geofence / Location
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                          <Chip
                            icon={<GpsIcon fontSize="small" />}
                            label={`Lat: ${organization.latitude ? parseFloat(organization.latitude).toFixed(5) : 'N/A'}`}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            icon={<GpsIcon fontSize="small" />}
                            label={`Lng: ${organization.longitude ? parseFloat(organization.longitude).toFixed(5) : 'N/A'}`}
                            variant="outlined"
                            size="small"
                          />
                          <Chip
                            icon={<LocationIcon fontSize="small" />}
                            label={`Radius: ${organization.geofence_radius || 100}m`}
                            variant="outlined"
                            color="primary"
                            size="small"
                          />
                        </Box>
                        <GeofenceMapPreview
                          latitude={organization.latitude}
                          longitude={organization.longitude}
                          radius={organization.geofence_radius || 100}
                        />
                        <Divider sx={{ my: 2 }} />
                      </>
                    )}

                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Quick Stats
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Card variant="outlined" sx={{ boxShadow: 'none', bgcolor: 'transparent' }}>
                          <CardContent sx={{ textAlign: 'center', p: 2 }}>
                            <PeopleIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h5" fontWeight="bold">
                              {organization.total_employees || 0}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Employees
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid size={6}>
                        <Card variant="outlined" sx={{ boxShadow: 'none', bgcolor: 'transparent' }}>
                          <CardContent sx={{ textAlign: 'center', p: 2 }}>
                            <DevicesIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h5" fontWeight="bold">
                              {organization.total_devices || 0}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Devices
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {tabValue === 1 && (
              <UsersManagement organizationId={id} />
            )}

            {tabValue === 2 && (
              <DeviceManager organizationId={id} />
            )}

            {tabValue === 3 && (
              <Paper sx={{ p: 3 }} elevation={0}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Attendance Overview (Last 7 Days)
                </Typography>
                <Box sx={{ height: 300, mb: 4 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    {attendanceLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        Loading...
                      </Box>
                    ) : (
                      <LineChart data={attendanceData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="present"
                          stroke="#1976d2"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="absent"
                          stroke="#dc004e"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Attendance Records
                </Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Total Employees</TableCell>
                        <TableCell>Present</TableCell>
                        <TableCell>Absent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendanceLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">Loading...</TableCell>
                        </TableRow>
                      ) : attendanceData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No attendance records found</TableCell>
                        </TableRow>
                      ) : (
                        [...attendanceData].sort((a, b) => new Date(b.date) - new Date(a.date)).map((row) => (
                          <TableRow key={row.date}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{row.total_employees}</TableCell>
                            <TableCell>{row.present}</TableCell>
                            <TableCell>{row.absent}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {tabValue === 4 && (
              <Paper sx={{ p: 3 }} elevation={0}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Organization Settings
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Organization Name"
                      fullWidth
                      margin="normal"
                      value={organization.name}
                      InputProps={{ readOnly: true }}
                      helperText={`Code: ${organization.org_code}`}
                    />
                    <TextField
                      label="Email"
                      fullWidth
                      margin="normal"
                      value={organization.contact_email}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label="Contact Person"
                      fullWidth
                      margin="normal"
                      value={organization.contact_name}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label="Phone"
                      fullWidth
                      margin="normal"
                      value={organization.contact_phone}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Address"
                      fullWidth
                      margin="normal"
                      value={organization.address}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label="Subscription Plan"
                      fullWidth
                      margin="normal"
                      value={organization.subscription_plan}
                      InputProps={{ readOnly: true, style: { textTransform: 'capitalize' } }}
                    />
                    <TextField
                      label="Status"
                      fullWidth
                      margin="normal"
                      value={organization.status}
                      InputProps={{ readOnly: true, style: { textTransform: 'capitalize' } }}
                    />
                    <TextField
                      label="Created At"
                      fullWidth
                      margin="normal"
                      value={formatDate(organization.created_at, 'dd/MM/yyyy')}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setEditDialogOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Edit Organization Details
                </Button>
              </Paper>
            )}
          </Box>
        </Paper>
      </Paper>
    </Box>
  )
}

export default OrganizationDetails